import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationService } from '../navigation.service';
import { UtilService } from '@app/shared/services/utils/util.service';
import { MenuDisplayMode } from '../components/navigation-menu/navigation-menu.interface';
import { BasicButtonComponent } from '@app/shared/components/buttons/basic-button/basic-button.component';
import { allowedOrgsSupportCenter, externalNavItems, mobileFooterNavItems } from '../navigation-items';
import { MatSidenav } from '@angular/material/sidenav';
import { INavigationItem } from '../interfaces';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { MixpanelService } from '@app/shared/services/mixpanel/mixpanel.service';
import { mixpanelEventTypes } from '@app/shared/enums/mixpanel.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, delay, filter, map, startWith, tap } from 'rxjs';
import { WidgetsService } from '@app/shared/components/cm-widgets/services/widgets.service';
import { UserService } from '@app/admin-station/work-center/user/services/user.service';
import { WidgetViewStates } from '../../shared/components/cm-widgets/constants/interfaces';

@Component({
  selector: 'cm-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: [
    './navigation.component.scss',
    '../components/menu-navigation-item/menu-navigation-item.component.scss',
    '../../styles/base/_typography.scss',
  ],
})
@UntilDestroy()
export class NavigationComponent implements OnInit, AfterViewInit {
  @ViewChild('toggleButton', { static: true }) toggleButton: ElementRef;
  @ViewChild('sidenav', { static: false }) sidenav: MatSidenav;

  navigationBarElement: HTMLElement;
  toggleButtonElement: BasicButtonComponent;
  externalNavItems: INavigationItem[];
  mobileFooterNavItems = mobileFooterNavItems;
  selectedSection: string;
  isDistributor = false;
  isMobile: boolean;
  isLoading: boolean;
  disableCollapse: boolean;
  isWidgetPage: boolean;
  timestamp = Date.now();
  widgetViewStates = WidgetViewStates;

  constructor(
    private authService: AuthService,
    private mixpanelService: MixpanelService,
    public navigationService: NavigationService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private userService: UserService,
    private widgetService: WidgetsService
  ) {
    this.isMobile = true;
    this.isLoading = true;
  }

  @HostListener('window:resize', [])
  public onResize(): void {
    this.isMobile = UtilService.isMobileAndTabletSize();

    if (this.isMobile) {
      setTimeout(() => {
        this.navigationService.sidenavState.next(false);
      }, 100);

      if (this.isMenuShrinked) {
        this.onShrinkMenu();
      };
    }

    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.onResize();
    }, 100);
  }

  ngOnInit(): void {
    // Checks for current route
    const route$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        const urlSegments = event.urlAfterRedirects.split('/');
        return urlSegments[urlSegments.length - 1];
      })
    );
    // If current route is Home and widgets are loading disable collapse
    combineLatest([
      route$,
      this.widgetService.viewState$,
    ]).pipe(delay(100)).subscribe(([lastRoute, widgetState]) => {
      this.isWidgetPage = lastRoute === 'home';
      this.disableCollapse = this.isWidgetPage && widgetState == this.widgetViewStates.LOADING;
    });
    // 
    this.navigationService.sidenavState.subscribe((state) => {
      this.sidenav?.toggle(state);
    });

    // Build external nav items
    this.userService.userLoginData$
      .pipe(
        untilDestroyed(this),
        tap(loginData => loginData && (this.isLoading = false))
      )
      .subscribe((loginData) => {
        if (!loginData) return;

        this.isDistributor = loginData.isDistributor;
          this.externalNavItems = externalNavItems.filter((externalLink) => {
            // Contidions for each external link if needed
            const keysMap = {
              support_center: () => allowedOrgsSupportCenter.some((o) => o == loginData.rootOrgId),
              sol_gen: () => this.isDistributor
            }
            return keysMap[externalLink.key] ? keysMap[externalLink.key]() : true;
          });
        });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        startWith(this.router)
      )
      .subscribe((event: any) => {
        const url = event.url === '/' ? 'work-center' : event.url.substring(1);
        this.navigationService.getItemByUrl(url, [
          ...this.navigationService.menuNavItems,
          ...this.navigationService.favoriteMenuItems,
          ...this.navigationService.menuNavFixedItemsValue,
        ]);
      });
  }

  public logout(): void {
    this.authService.logout();
  }

  changeSidenavState(state: boolean): void {
    if (this.isMobile) this.navigationService.sidenavState.next(state);
  }

  onShrinkMenu() {
    this.navigationService.onMenuShrink();
    // Only trigger refresh on widget page
    this.isWidgetPage && this.widgetService.refreshWidgets$.next(true);
  }

  get isMenuShrinked() {
    return this.navigationService.isShrinked;
  }

  get buttonIcon() {
    return this.navigationService.isShrinked ? 'double_arrow' : 'double_arrow';
  }

  get menuDisplayMode(): MenuDisplayMode {
    return this.navigationService.isShrinked ? MenuDisplayMode.SHRINKED : MenuDisplayMode.EXPANDED;
  }

  onMenuExpanded(section: string): void {
    this.mixpanelService.trackEvent(mixpanelEventTypes.SIDEBAR_EXPAND, {
      category: section,
    });
  }

  onMenuCollapsed(section: string): void {
    this.mixpanelService.trackEvent(mixpanelEventTypes.SIDEBAR_COLLAPSE, {
      category: section,
    });
  }
}
